import { OperadorGrantRow, OperadorPinDialog, useOperadorGrant } from '../../../components/pos/OperadorPinDialog';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchCitaById, patchCita, Cita, fetchEspacioServicios, fetchPrecioPlanesServicios, fetchCategories } from '../../../services/citasService';
import {
    Calendar, User, Building2, Tag, Users,
    CheckCircle2, XCircle, AlertCircle, ChevronLeft, Printer,
    Download, Edit, Trash2, Mail, CreditCard, CalendarDays, Clock as ClockIcon, Receipt, UserCheck, FileText, ArrowLeft,
    Package, MapPin, Layers, Sparkles, ShieldCheck, Save
} from 'lucide-react';

import CancelarReservaPanel, { bloqueoCancelacion } from '../../../components/posCita/CancelarReservaPanel';
import ProductosReservaDetalle from '../../../components/posCita/ProductosReservaDetalle';
import TrazabilidadReserva from '../../../components/posCita/TrazabilidadReserva';

const DetallesReservaPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [cita, setCita] = useState<Cita | null>(null);
    const operador = useOperadorGrant(cita?.sesion_pos ?? null);
    const [cancelando, setCancelando] = useState(false);
    const [cancelacionExitosa, setCancelacionExitosa] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [espacioInfo, setEspacioInfo] = useState<any>(null);
    const [precioPlanInfo, setPrecioPlanInfo] = useState<any>(null);
    const [categoriaInfo, setCategoriaInfo] = useState<any>(null);
    const [loadingDetails, setLoadingDetails] = useState(true);

    // Estados para control de ingreso, salida y limpieza
    const [isEditingObservaciones, setIsEditingObservaciones] = useState(false);
    const [obsIngreso, setObsIngreso] = useState('');
    const [obsSalida, setObsSalida] = useState('');
    const [limpIngreso, setLimpIngreso] = useState(true);
    const [limpSalida, setLimpSalida] = useState(true);
    const [isSavingObs, setIsSavingObs] = useState(false);
    const [obsSuccessMsg, setObsSuccessMsg] = useState<string | null>(null);

    // --- PALETA DE COLORES COMARCA ---
    const theme = {
        primaryAction: '#c1348b',  // Magenta
        corporate: '#8f1f64',      // Vino
        highlightText: '#ea8406',  // Naranja Oscuro
        highlightBg: '#ffd97c',    // Amarillo Claro
        blue: '#61257b',           // Azul
        teal: '#8b3fa8',           // Turquesa
    };

    useEffect(() => {
        const loadCita = async () => {
            if (!id) return;

            setIsLoading(true);
            setError(null);

            try {
                const resultado = await fetchCitaById(parseInt(id));
                setCita(resultado);
                setObsIngreso(resultado.observaciones_ingreso || '');
                setObsSalida(resultado.observaciones_salida || '');
                setLimpIngreso(resultado.limpieza_ingreso ?? true);
                setLimpSalida(resultado.limpieza_salida ?? true);

                await loadAdditionalInfo(resultado);
            } catch (error) {
                console.error("Error cargando cita:", error);
                setError("No se pudo cargar los detalles de la reserva");
            } finally {
                setIsLoading(false);
                setLoadingDetails(false);
            }
        };

        const loadAdditionalInfo = async (citaData: Cita) => {
            try {
                // Cargar información del espacio de servicio
                if (citaData.espacio_servicio) {
                    const espacios = await fetchEspacioServicios();
                    const espacio = espacios.find(e => e.id === citaData.espacio_servicio);
                    if (espacio) {
                        setEspacioInfo(espacio);
                    }
                }

                const precios = await fetchPrecioPlanesServicios();
                const precioPlan = precios.find(p => p.id === citaData.precio_plan_servicio);
                if (precioPlan) {
                    setPrecioPlanInfo(precioPlan);
                }

                if (citaData.categoria) {
                    const categorias = await fetchCategories();
                    const categoria = categorias.find((c: any) => c.id === citaData.categoria);
                    if (categoria) {
                        setCategoriaInfo(categoria);
                    }
                }
            } catch (error) {
                console.error("Error cargando información adicional:", error);
            }
        };

        loadCita();
    }, [id]);

    const getEstadoBadgeStyles = (estado: string) => {
        const base = "inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold tracking-wide border";

        switch (estado.toLowerCase()) {
            case 'confirmada':
            case 'completada':
                return `${base} bg-green-50 text-green-700 border-green-200`;
            case 'pendiente':
                return `${base} bg-yellow-50 text-yellow-700 border-yellow-200`;
            case 'cancelada':
                return `${base} bg-red-50 text-red-700 border-red-200`;
            case 'en_proceso':
                return `${base} bg-blue-50 text-blue-700 border-blue-200`;
            default:
                return `${base} bg-gray-50 text-gray-600 border-gray-200`;
        }
    };

    const getEstadoPagoBadgeStyles = (estadoPago: string) => {
        const base = "inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold tracking-wide border";

        switch (estadoPago.toLowerCase()) {
            case 'pagado':
            case 'completado':
                return `${base} bg-green-50 text-green-700 border-green-200`;
            case 'parcial':
                return `${base} bg-blue-50 text-blue-700 border-blue-200`;
            case 'pendiente':
            default:
                return `${base} bg-yellow-50 text-yellow-700 border-yellow-200`;
        }
    };

    // FUNCIONES CORREGIDAS PARA MANEJAR ZONA HORARIA
    const parseDateString = (dateString?: string | null): Date | null => {
        if (!dateString) return null;

        // Si la fecha viene con Z (UTC), mantenerla como UTC
        if (dateString.endsWith('Z')) {
            return new Date(dateString);
        }

        // Si no tiene Z, asumir que está en UTC y agregar 'Z' para forzar interpretación UTC
        // Esto previene que el navegador la interprete como hora local
        return new Date(dateString.includes('T') ? dateString + 'Z' : dateString + 'T00:00:00Z');
    };

    const formatDate = (dateString?: string | null) => {
        if (!dateString) return 'No programada';
        try {
            const date = parseDateString(dateString);
            if (!date || isNaN(date.getTime())) return 'Fecha inválida';

            // Usar UTC para evitar cambios por zona horaria
            return date.toLocaleDateString('es-ES', {
                timeZone: 'UTC',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                weekday: 'long'
            });
        } catch (error) {
            console.error('Error formateando fecha:', error, dateString);
            return 'Fecha inválida';
        }
    };

    const formatTime = (timeString?: string | null) => {
        if (!timeString) return 'Sin hora';
        try {
            return timeString.substring(0, 5); // Formato HH:MM
        } catch (error) {
            return 'Hora inválida';
        }
    };

    const formatDateRange = (startDate?: string | null, endDate?: string | null) => {
        if (!startDate) return 'No programada';

        const start = parseDateString(startDate);
        if (!start || isNaN(start.getTime())) return 'Fecha inválida';

        // Formatear fecha de inicio en UTC
        const startFormatted = start.toLocaleDateString('es-ES', {
            timeZone: 'UTC',
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });

        if (!endDate) return startFormatted;

        const end = parseDateString(endDate);
        if (!end || isNaN(end.getTime())) return startFormatted;

        // Formatear fecha de fin en UTC
        const endFormatted = end.toLocaleDateString('es-ES', {
            timeZone: 'UTC',
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });

        return `${startFormatted} - ${endFormatted}`;
    };

    // CAMBIADO: Función para formatear moneda con "Bs" en lugar de "BOB"
    const formatCurrency = (amount: number) => {
        return `Bs ${amount.toLocaleString('es-ES', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };

    const getEstadoIcon = (estado: string) => {
        switch (estado.toLowerCase()) {
            case 'confirmada':
            case 'completada':
                return <CheckCircle2 className="h-5 w-5" />;
            case 'cancelada':
                return <XCircle className="h-5 w-5" />;
            case 'pendiente':
            case 'en_proceso':
                return <AlertCircle className="h-5 w-5" />;
            default:
                return <AlertCircle className="h-5 w-5" />;
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const handleSaveObservaciones = async () => {
        if (!cita) return;
        if (!operador.token) { operador.openDialog(); return; }
        setIsSavingObs(true);
        try {
            const updated = await patchCita(cita.id, {
                observaciones_ingreso: obsIngreso,
                observaciones_salida: obsSalida,
                limpieza_ingreso: limpIngreso,
                limpieza_salida: limpSalida
            }, operador.token);
            setCita(prev => prev ? { ...prev, ...updated } : null);
            setIsEditingObservaciones(false);
            setObsSuccessMsg('Observaciones y estado de limpieza actualizados con éxito');
            setTimeout(() => setObsSuccessMsg(null), 3500);
        } catch (err) {
            console.error('Error guardando observaciones:', err);
        } finally {
            setIsSavingObs(false);
        }
    };

    // Función para debug: mostrar fecha en diferentes formatos
    const debugDate = (dateString?: string | null) => {
        if (!dateString) return null;
        const date = parseDateString(dateString);
        if (!date) return null;

        return {
            original: dateString,
            utc: date.toISOString(),
            local: date.toLocaleString('es-ES'),
            day: date.getUTCDate()
        };
    };

    if (isLoading || loadingDetails) {
        return (
            <div className="min-h-screen bg-gray-50/50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: theme.primaryAction }}></div>
            </div>
        );
    }

    if (error || !cita) {
        return (
            <div className="min-h-screen bg-gray-50/50 flex items-center justify-center">
                <div className="text-center">
                    <div className="mx-auto h-16 w-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
                        <XCircle className="h-8 w-8 text-red-500" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Error al cargar la reserva</h3>
                    <p className="text-gray-600 mb-6">{error || "La reserva no existe"}</p>
                    <button
                        onClick={() => navigate('/reservas')}
                        className="px-4 py-2 bg-[#8f1f64] text-white rounded-lg hover:bg-[#c1348b] transition-colors flex items-center gap-2 mx-auto"
                    >
                        <ChevronLeft className="h-4 w-4" />
                        Volver a reservas
                    </button>
                </div>
            </div>
        );
    }

    const bloqueo = bloqueoCancelacion(cita);
    const abrirCancelacion = () => {
        setCancelando(true);
        window.setTimeout(() => document.getElementById('cancelacion-reserva')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0);
    };

    // Debug: mostrar información de fechas
    console.log('Fecha inicio debug:', debugDate(cita.fecha_inicio));
    console.log('Fecha fin debug:', debugDate(cita.fecha_fin));

    return (
        <div className="min-h-screen bg-gray-50/50">
            <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl pt-8 pb-16">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <button
                                onClick={() => navigate('/reservas')}
                                className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#8f1f64] mb-4 transition-colors"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Volver a reservas
                            </button>

                            <div className="flex items-center gap-3 mb-2">
                                <div className="h-10 w-10 rounded-lg flex items-center justify-center"
                                    style={{ backgroundColor: theme.corporate }}>
                                    <Receipt className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900">
                                        Reserva: {cita.codigo || `CITA-${cita.id}`}
                                    </h1>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={handlePrint}
                                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 transition-all"
                            >
                                <Printer className="h-4 w-4" />
                                Imprimir
                            </button>
                            <button className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 transition-all">
                                <Download className="h-4 w-4" />
                                Exportar
                            </button>
                        </div>
                    </div>

                    <ProductosReservaDetalle cita={cita} showEmpty/>
                    {cancelacionExitosa && <p role="status" className="my-4 rounded-xl bg-green-50 p-4 text-green-800">Reserva cancelada correctamente. El horario quedó disponible.</p>}
                    {bloqueo && <p className="my-3 text-sm text-slate-600">{bloqueo}</p>}
                    <div id="cancelacion-reserva">{cancelando && <CancelarReservaPanel key={cita.id} cita={cita} token={operador.token} onIdentify={operador.openDialog} onClose={() => setCancelando(false)} onSuccess={actualizada => { setCita(actualizada); setCancelando(false); setCancelacionExitosa(true); setIsEditingObservaciones(false); }}/>}</div><div className="mb-6"><TrazabilidadReserva citaId={cita.id}/></div>
                    {/* Estado y acciones rápidas */}
                    <div className="flex flex-wrap items-center justify-between gap-4 p-6 bg-white rounded-xl border border-gray-200 shadow-sm mb-8">
                        <div className="flex flex-wrap items-center gap-4">
                            <div>
                                <p className="text-sm font-medium text-gray-500 mb-1">Estado</p>
                                <span className={getEstadoBadgeStyles(cita.estado)}>
                                    <span className="flex items-center gap-2">
                                        {getEstadoIcon(cita.estado)}
                                        {cita.estado || 'Sin estado'}
                                    </span>
                                </span>
                            </div>

                            <div>
                                <p className="text-sm font-medium text-gray-500 mb-1">Estado de pago</p>
                                <span className={getEstadoPagoBadgeStyles(cita.estado_pago)}>
                                    {cita.estado_pago || 'Sin pago'}
                                </span>
                            </div>

                            <div>
                                <p className="text-sm font-medium text-gray-500 mb-1">Monto total</p>
                                <p className="text-xl font-bold text-gray-900">
                                    {formatCurrency(cita.total)}
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <button
                                disabled={cita.estado === 'Cancelada'}
                                onClick={() => navigate(`/reservas/editar/${cita.id}`)}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-all"
                                style={{ backgroundColor: theme.primaryAction }}
                            >
                                <Edit className="h-4 w-4" />
                                Editar
                            </button>
                            <button onClick={abrirCancelacion} disabled={!!bloqueo} title={bloqueo || undefined} className="disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 px-4 py-2 border border-red-300 rounded-lg text-red-600 hover:bg-red-50 transition-all">
                                <Trash2 className="h-4 w-4" />
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>

                {/* Grid de información */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Columna izquierda - Información principal */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Información del cliente */}
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <User className="h-5 w-5" style={{ color: theme.corporate }} />
                                    Información del cliente
                                </h2>
                            </div>
                            <div className="p-6">
                                {cita.cliente_info ? (
                                    <div className="space-y-4">
                                        <div className="flex items-start gap-4">
                                            <div className="h-12 w-12 rounded-full flex items-center justify-center text-lg font-bold text-white"
                                                style={{ backgroundColor: theme.corporate }}>
                                                {cita.cliente_info.first_name?.[0]}{cita.cliente_info.last_name?.[0]}
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="text-lg font-bold text-gray-900">
                                                    {cita.cliente_info.first_name} {cita.cliente_info.last_name}
                                                </h3>
                                                {/* Tipo de cliente */}
                                                {(cita.cliente_info as any).tipo_nivel_usuario?.nombre && (
                                                    <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-bold"
                                                        style={{ background: `${theme.blue}15`, color: theme.blue }}>
                                                        {(cita.cliente_info as any).tipo_nivel_usuario.nombre}
                                                    </span>
                                                )}
                                                <div className="flex items-center gap-4 mt-2">
                                                    {cita.cliente_info.email && (
                                                        <div className="flex items-center gap-2 text-gray-600">
                                                            <Mail className="h-4 w-4" />
                                                            <span className="text-sm">{cita.cliente_info.email}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {/* Declaración Jurada status */}
                                        {(cita.cliente_info as any).declaracion_jurada_firmada ? (
                                            <div className="flex items-center gap-3 p-3 rounded-xl border"
                                                style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                                                <CheckCircle2 className="h-5 w-5 flex-shrink-0" style={{ color: '#16a34a' }} />
                                                <div>
                                                    <p className="text-sm font-bold" style={{ color: '#15803d' }}>Declaración Jurada Acreditada</p>
                                                    <p className="text-xs" style={{ color: '#166534' }}>
                                                        {(cita.cliente_info as any).fecha_declaracion_jurada
                                                            ? `Firmada el ${new Date((cita.cliente_info as any).fecha_declaracion_jurada).toLocaleDateString('es-ES')}`
                                                            : 'No requiere firmar nuevamente'}
                                                    </p>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-3 p-3 rounded-xl border"
                                                style={{ background: '#fffbeb', borderColor: '#fde68a' }}>
                                                <AlertCircle className="h-5 w-5 flex-shrink-0" style={{ color: '#d97706' }} />
                                                <div>
                                                    <p className="text-sm font-bold" style={{ color: '#92400e' }}>Declaración Jurada Pendiente</p>
                                                    <p className="text-xs" style={{ color: '#78350f' }}>Se debe registrar la firma del cliente</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-gray-500">Cliente no especificado</p>
                                )}
                            </div>
                        </div>

                        {/* Detalles de la reserva */}
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <CalendarDays className="h-5 w-5" style={{ color: theme.corporate }} />
                                    Detalles de la reserva
                                </h2>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                                                <Calendar className="h-4 w-4" />
                                                Fecha de la reserva
                                            </p>
                                            <p className="text-gray-900 font-medium">
                                                {formatDateRange(cita.fecha_inicio, cita.fecha_fin)}
                                            </p>
                                        </div>

                                        <div>
                                            <p className="text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                                                <ClockIcon className="h-4 w-4" />
                                                Horario
                                            </p>
                                            <p className="text-gray-900 font-medium">
                                                {formatTime(cita.hora_inicio)}
                                                {cita.hora_fin && ` - ${formatTime(cita.hora_fin)}`}
                                            </p>
                                        </div>

                                        <div>
                                            <p className="text-sm font-medium text-gray-500 mb-1">Duración</p>
                                            <p className="text-gray-900 font-medium">
                                                {cita.cantidad_periodos ? `${cita.cantidad_periodos} periodo(s)` : 'No especificada'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 mb-1">Tipo de días</p>
                                            <p className="text-gray-900 font-medium">
                                                {cita.dias_habiles ? 'Días hábiles' : 'Todos los días'}
                                            </p>
                                        </div>

                                        {cita.sucursal_info && (
                                            <div>
                                                <p className="text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                                                    <Building2 className="h-4 w-4" />
                                                    Sucursal
                                                </p>
                                                <p className="text-gray-900 font-medium">
                                                    {cita.sucursal_info.nombre}
                                                </p>
                                            </div>
                                        )}

                                        <div>
                                            <p className="text-sm font-medium text-gray-500 mb-1">Categoría</p>
                                            <p className="text-gray-900 font-medium">
                                                {categoriaInfo?.nombre || cita.categoria || 'No especificada'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Información de servicio y espacio */}
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <Package className="h-5 w-5" style={{ color: theme.corporate }} />
                                    Información de servicio
                                </h2>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Información del plan y servicio */}
                                    <div className="space-y-4">
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 mb-1">Plan de servicio</p>
                                            <div className="bg-gray-50 p-4 rounded-lg">
                                                {precioPlanInfo ? (
                                                    <div className="space-y-2">
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm text-gray-600">Servicio:</span>
                                                            <span className="font-medium">{precioPlanInfo.servicio_nombre}</span>
                                                        </div>
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm text-gray-600">Plan:</span>
                                                            <span className="font-medium">{precioPlanInfo.plan_nombre}</span>
                                                        </div>
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm text-gray-600">Precio inicial:</span>
                                                            <span className="font-medium text-green-600">
                                                                {formatCurrency(precioPlanInfo.precio_inicial)}
                                                            </span>
                                                        </div>
                                                        {precioPlanInfo.precio_posterior && (
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm text-gray-600">Precio posterior:</span>
                                                                <span className="font-medium">
                                                                    {formatCurrency(precioPlanInfo.precio_posterior)}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-500 text-sm">Información del plan no disponible</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Información del espacio */}
                                    <div className="space-y-4">
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 mb-1">Espacio asignado</p>
                                            <div className="bg-gray-50 p-4 rounded-lg">
                                                {espacioInfo ? (
                                                    <div className="space-y-2">
                                                        <div className="flex items-center gap-2">
                                                            <MapPin className="h-4 w-4 text-gray-400" />
                                                            <span className="font-medium">{espacioInfo.espacio_nombre}</span>
                                                        </div>
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm text-gray-600">Capacidad:</span>
                                                            <span className="font-medium">{espacioInfo.cantidad_personas} personas</span>
                                                        </div>
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm text-gray-600">Servicio:</span>
                                                            <span className="font-medium">{espacioInfo.servicio_nombre}</span>
                                                        </div>
                                                    </div>
                                                ) : cita.espacio_servicio ? (
                                                    <p className="text-gray-500 text-sm">
                                                        Espacio ID: {cita.espacio_servicio} (Información no disponible)
                                                    </p>
                                                ) : (
                                                    <p className="text-gray-500 text-sm">No se asignó espacio específico</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Información de pago */}
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <CreditCard className="h-5 w-5" style={{ color: theme.corporate }} />
                                    Información de pago
                                </h2>
                            </div>
                            <div className="p-6">
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <p className="text-sm font-medium text-gray-500 mb-1">Subtotal</p>
                                            <p className="text-lg font-semibold text-gray-900">
                                                {formatCurrency(cita.subtotal || cita.total)}
                                            </p>
                                        </div>

                                        {cita.descuento_directo && cita.descuento_directo > 0 && (
                                            <div>
                                                <p className="text-sm font-medium text-gray-500 mb-1 flex items-center gap-2">
                                                    <Tag className="h-4 w-4" />
                                                    Descuento aplicado
                                                </p>
                                                <p className="text-lg font-semibold text-red-600">
                                                    -{formatCurrency(cita.descuento_directo)}
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    <div className="pt-4 border-t border-gray-200">
                                        <div className="flex justify-between items-center">
                                            <p className="text-lg font-medium text-gray-900">Total</p>
                                            <p className="text-2xl font-bold" style={{ color: theme.corporate }}>
                                                {formatCurrency(cita.total)}
                                            </p>
                                        </div>
                                    </div>

                                    {cita.monto_pagado !== undefined && (
                                        <div className="pt-4 border-t border-gray-200">
                                            <div className="flex justify-between items-center">
                                                <p className="text-sm font-medium text-gray-500">Monto pagado</p>
                                                <p className="text-lg font-semibold text-green-600">
                                                    {formatCurrency(cita.monto_pagado)}
                                                </p>
                                            </div>
                                            <div className="flex justify-between items-center mt-2">
                                                <p className="text-sm font-medium text-gray-500">Saldo pendiente</p>
                                                <p className="text-lg font-semibold text-orange-600">
                                                    {formatCurrency(cita.total - cita.monto_pagado)}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Clientes asociados */}
                        {cita.clientes_asociados_info && cita.clientes_asociados_info.length > 0 && (
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                        <Users className="h-5 w-5" style={{ color: theme.corporate }} />
                                        Clientes asociados
                                        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-sm font-normal">
                                            {cita.clientes_asociados_info.length}
                                        </span>
                                    </h2>
                                </div>
                                <div className="p-6">
                                    <div className="space-y-4">
                                        {cita.clientes_asociados_info.map((cliente, index) => (
                                            <div key={cliente.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium text-white"
                                                        style={{ backgroundColor: theme.blue }}>
                                                        {cliente.first_name?.[0]}{cliente.last_name?.[0]}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-900">
                                                            {cliente.first_name} {cliente.last_name}
                                                        </p>
                                                        <p className="text-sm text-gray-500">{cliente.email}</p>
                                                    </div>
                                                </div>
                                                <span className="text-xs font-medium px-2 py-1 bg-gray-100 text-gray-600 rounded">
                                                    Asociado {index + 1}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Control de Ingreso, Salida y Limpieza */}
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-slate-50/60">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <Sparkles className="h-5 w-5" style={{ color: theme.corporate }} />
                                    Control de Ingreso, Salida y Limpieza
                                </h2>
                                {!isEditingObservaciones ? (
                                    <button
                                        disabled={cita.estado === 'Cancelada'}
                                        onClick={() => setIsEditingObservaciones(true)}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-all shadow-sm"
                                        style={{ backgroundColor: theme.corporate }}
                                    >
                                        <Edit className="h-3.5 w-3.5" />
                                        Editar / Registrar
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => setIsEditingObservaciones(false)}
                                        className="text-xs font-semibold text-gray-500 hover:text-gray-800 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                )}
                            </div>

                            <div className="p-6">
                                <OperadorGrantRow grant={operador.grant} onIdentify={operador.openDialog} disabled={isSavingObs} />
                                <OperadorPinDialog open={operador.dialogOpen} sesionId={cita.sesion_pos ?? null} onClose={operador.closeDialog} onGrant={operador.install} />
                                {obsSuccessMsg && (
                                    <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-xs font-bold flex items-center gap-2">
                                        <CheckCircle2 size={16} />
                                        <span>{obsSuccessMsg}</span>
                                    </div>
                                )}

                                {!isEditingObservaciones ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Estado Ingreso */}
                                        <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/50 space-y-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Ingreso del Cliente</span>
                                                {cita.limpieza_ingreso ?? true ? (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                        <CheckCircle2 size={12} /> Espacio Limpio
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                                        <AlertCircle size={12} /> Con Observaciones
                                                    </span>
                                                )}
                                            </div>
                                            <div>
                                                <p className="text-xs font-semibold text-gray-400 mb-1">Notas de ingreso:</p>
                                                <p className="text-sm text-gray-800 bg-white p-3 rounded-lg border border-gray-200/80 min-h-[60px] whitespace-pre-wrap">
                                                    {cita.observaciones_ingreso || 'Sin observaciones registradas al momento del ingreso.'}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Estado Salida */}
                                        <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/50 space-y-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Salida del Cliente</span>
                                                {cita.limpieza_salida ?? true ? (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                        <CheckCircle2 size={12} /> Entregado Limpio
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                                        <AlertCircle size={12} /> Con Observaciones
                                                    </span>
                                                )}
                                            </div>
                                            <div>
                                                <p className="text-xs font-semibold text-gray-400 mb-1">Notas de salida:</p>
                                                <p className="text-sm text-gray-800 bg-white p-3 rounded-lg border border-gray-200/80 min-h-[60px] whitespace-pre-wrap">
                                                    {cita.observaciones_salida || 'Sin observaciones registradas al momento de la salida.'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    /* Modo Edición */
                                    <div className="space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {/* Formulario Ingreso */}
                                            <div className="p-4 rounded-xl border border-gray-200 bg-white space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm font-bold text-gray-800 flex items-center gap-2">
                                                        <CheckCircle2 size={16} className="text-emerald-600" />
                                                        Ingreso del Cliente
                                                    </span>
                                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                                        <input
                                                            type="checkbox"
                                                            checked={limpIngreso}
                                                            onChange={e => setLimpIngreso(e.target.checked)}
                                                            className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 border-gray-300"
                                                        />
                                                        <span className="text-xs font-bold text-gray-700">Espacio limpio al ingresar</span>
                                                    </label>
                                                </div>
                                                <textarea
                                                    rows={3}
                                                    value={obsIngreso}
                                                    onChange={e => setObsIngreso(e.target.value)}
                                                    placeholder="Escribe observaciones sobre el estado inicial del espacio, equipamiento, llaves o recepción..."
                                                    className="w-full text-sm p-3 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:outline-none focus:border-[#8f1f64] transition-all resize-none"
                                                />
                                            </div>

                                            {/* Formulario Salida */}
                                            <div className="p-4 rounded-xl border border-gray-200 bg-white space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm font-bold text-gray-800 flex items-center gap-2">
                                                        <ShieldCheck size={16} className="text-blue-600" />
                                                        Salida del Cliente
                                                    </span>
                                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                                        <input
                                                            type="checkbox"
                                                            checked={limpSalida}
                                                            onChange={e => setLimpSalida(e.target.checked)}
                                                            className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 border-gray-300"
                                                        />
                                                        <span className="text-xs font-bold text-gray-700">Espacio entregado limpio</span>
                                                    </label>
                                                </div>
                                                <textarea
                                                    rows={3}
                                                    value={obsSalida}
                                                    onChange={e => setObsSalida(e.target.value)}
                                                    placeholder="Escribe observaciones sobre el estado final en que dejó el espacio, llaves devueltas o incidencias..."
                                                    className="w-full text-sm p-3 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:outline-none focus:border-[#8f1f64] transition-all resize-none"
                                                />
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
                                            <button
                                                type="button"
                                                onClick={() => setIsEditingObservaciones(false)}
                                                className="px-4 py-2 border border-gray-300 text-gray-700 text-xs font-bold rounded-lg hover:bg-gray-50 transition-colors"
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                type="button"
                                                disabled={isSavingObs}
                                                onClick={handleSaveObservaciones}
                                                className="px-5 py-2 text-white text-xs font-bold rounded-lg transition-all shadow-md flex items-center gap-2 disabled:opacity-50"
                                                style={{ backgroundColor: theme.primaryAction }}
                                            >
                                                <Save size={14} />
                                                {isSavingObs ? 'Guardando...' : 'Guardar Observaciones'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Columna derecha - Información adicional y acciones */}
                    <div className="space-y-8">
                        {/* Resumen de la reserva */}
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <Layers className="h-5 w-5" />
                                    Resumen
                                </h2>
                            </div>
                            <div className="p-6">
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Código de reserva</span>
                                        <span className="font-medium">{cita.codigo || `CITA-${cita.id}`}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Fecha de creación</span>
                                        <span className="font-medium">{formatDate(cita.fecha_inicio)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Espacio de servicio</span>
                                        <span className="font-medium text-right">
                                            {espacioInfo ? espacioInfo.espacio_nombre : `ID: ${cita.espacio_servicio || 'N/A'}`}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Plan de servicio</span>
                                        <span className="font-medium text-right">
                                            {precioPlanInfo ?
                                                `${precioPlanInfo.servicio_nombre} - ${precioPlanInfo.plan_nombre}` :
                                                `ID: ${cita.precio_plan_servicio}`}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Categoría</span>
                                        <span className="font-medium text-right">
                                            {categoriaInfo ? categoriaInfo.nombre : `ID: ${cita.categoria || 'N/A'}`}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Acciones rápidas */}
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900">Acciones rápidas</h2>
                            </div>
                            <div className="p-6">
                                <div className="space-y-3">
                                    <button 
                                    disabled={cita.estado === 'Cancelada'}
                                    onClick={() => navigate(`/reservas/editar/${cita.id}`)}
                                    className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-[#8f1f64] hover:bg-[#8f1f64]/5 transition-all group disabled:opacity-50 disabled:cursor-not-allowed">
                                        <div className="flex items-center gap-3">
                                            <div className="h-8 w-8 rounded-lg flex items-center justify-center group-hover:bg-[#8f1f64] transition-colors"
                                                style={{ backgroundColor: theme.primaryAction + '20' }}>
                                                <Edit className="h-4 w-4" style={{ color: theme.primaryAction }} />
                                            </div>
                                            <span className="font-medium">Editar reserva</span>
                                        </div>
                                        <ChevronLeft className="h-4 w-4 rotate-180 text-gray-400" />
                                    </button>

                                    <button className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-green-500 hover:bg-green-50 transition-all group">
                                        <div className="flex items-center gap-3">
                                            <div className="h-8 w-8 rounded-lg flex items-center justify-center group-hover:bg-green-500 transition-colors bg-green-50">
                                                <CheckCircle2 className="h-4 w-4 text-green-600 group-hover:text-white" />
                                            </div>
                                            <span className="font-medium">Confirmar reserva</span>
                                        </div>
                                        <ChevronLeft className="h-4 w-4 rotate-180 text-gray-400" />
                                    </button>

                                    <button className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all group">
                                        <div className="flex items-center gap-3">
                                            <div className="h-8 w-8 rounded-lg flex items-center justify-center group-hover:bg-blue-500 transition-colors bg-blue-50">
                                                <CreditCard className="h-4 w-4 text-blue-600 group-hover:text-white" />
                                            </div>
                                            <span className="font-medium">Registrar pago</span>
                                        </div>
                                        <ChevronLeft className="h-4 w-4 rotate-180 text-gray-400" />
                                    </button>

                                    <button onClick={abrirCancelacion} disabled={!!bloqueo} title={bloqueo || undefined} className="disabled:opacity-50 disabled:cursor-not-allowed w-full flex items-center justify-between p-3 rounded-lg border border-red-200 hover:border-red-500 hover:bg-red-50 transition-all group">
                                        <div className="flex items-center gap-3">
                                            <div className="h-8 w-8 rounded-lg flex items-center justify-center group-hover:bg-red-500 transition-colors bg-red-50">
                                                <XCircle className="h-4 w-4 text-red-600 group-hover:text-white" />
                                            </div>
                                            <span className="font-medium text-red-600">Cancelar reserva</span>
                                        </div>
                                        <ChevronLeft className="h-4 w-4 rotate-180 text-red-400" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Notas */}
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <FileText className="h-5 w-5" />
                                    Notas
                                </h2>
                            </div>
                            <div className="p-6">
                                <p className="text-gray-600 italic">
                                    No hay notas adicionales para esta reserva.
                                </p>
                                <button className="mt-4 text-sm font-medium" style={{ color: theme.primaryAction }}>
                                    + Agregar nota
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Timeline de actividades */}
                <div className="mt-8 bg-white rounded-xl border border-gray-200 shadow-sm">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">Historial de la reserva</h2>
                    </div>
                    <div className="p-6">
                        <div className="relative">
                            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                            <div className="space-y-6">
                                <div className="relative pl-12">
                                    <div className="absolute left-0 top-0 h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900">Reserva creada</p>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {formatDate(cita.fecha_inicio)} a las {formatTime(cita.hora_inicio)}
                                        </p>
                                    </div>
                                </div>

                                <div className="relative pl-12">
                                    <div className="absolute left-0 top-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                        <UserCheck className="h-4 w-4 text-blue-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900">Cliente asignado</p>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {cita.cliente_info ?
                                                `${cita.cliente_info.first_name} ${cita.cliente_info.last_name}` :
                                                'Sin cliente asignado'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DetallesReservaPage;
